import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  TextField,
  Tabs,
  Tab,
  Button,
  Box,
} from "@mui/material";
import { db, auth } from "../../../firebase-config";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]); // Fetch categories from Firestore
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsRef = collection(db, "products");
        const snapshot = await getDocs(productsRef);
        const productList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProducts(productList);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products.");
      } finally {
        setLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const categoriesRef = collection(db, "Categories");
        const categoriesSnap = await getDocs(categoriesRef);
        const categoryList = categoriesSnap.docs.map((doc) => doc.data().name);
        setCategories(["all", ...categoryList]); // Include "All" as the first category
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };

    const checkAdminStatus = async () => {
      if (!currentUser) return;
      try {
        const adminRef = doc(db, "admins", currentUser.uid);
        const adminSnap = await getDoc(adminRef);
        setIsAdmin(adminSnap.exists());
      } catch (err) {
        console.error("Error checking admin status:", err);
      }
    };

    fetchProducts();
    fetchCategories();
    checkAdminStatus();
  }, [currentUser]);

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Product List</Typography>
        {isAdmin && (
          <Button variant="contained" color="primary" onClick={() => navigate("/admin/products/add")}>
            Add Product
          </Button>
        )}
      </Box>

      {/* Search Field */}
      <TextField
        label="Search Products"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {/* Category Tabs (Now Dynamic) */}
      <Tabs
        value={selectedCategory}
        onChange={(event, newValue) => setSelectedCategory(newValue)}
        sx={{ mb: 3 }}
      >
        {categories.map((category) => (
          <Tab key={category} label={category} value={category} />
        ))}
      </Tabs>

      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={3}>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={product.imageUrl || "https://via.placeholder.com/200"}
                    alt={product.name}
                  />
                  <CardContent>
                    <Typography variant="h6">{product.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {product.description ? product.description.slice(0, 60) + "..." : "No description available"}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      ${product.defaultPrice.toFixed(2)}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => navigate(`/admin/products/${product.id}`)}
                    >
                      View Product
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Typography variant="body1">No products found.</Typography>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default ProductList;
